import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  MenuItem,
  Paper,
} from '@mui/material';
import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import { MRT_Localization_EN } from 'material-react-table/locales/en';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { buildHistoryRowId } from './dealer-history/historyViewModel';
import {
  renderHistoryValue,
} from './dealer-history/historyValueRenderer.jsx';
import { scheduleMrtFilterPopoverOpen } from '../utils/mrtFilterPopover';

const DealerHistoryMrtTable = ({ rows, counts, isLoading = false }) => {
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([{ id: 'changedAtDisplay', desc: true }]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [columnOrder, setColumnOrder] = useState([]);
  const [columnPinning, setColumnPinning] = useState({});
  const [pendingFilterColumnId, setPendingFilterColumnId] = useState(null);

  const data = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'changedAtDisplay',
        header: 'Changed At',
        size: 190,
        sortingFn: (left, right) => (left.original.changedAtSort || 0) - (right.original.changedAtSort || 0),
      },
      { accessorKey: 'programName', header: 'Program', size: 150 },
      { accessorKey: 'field', header: 'Field', size: 180 },
      { accessorKey: 'selectionProductCode', header: 'Product Code', size: 150 },
      { accessorKey: 'selectionReferenceId', header: 'Reference ID', size: 220 },
      {
        accessorKey: 'previousValue',
        header: 'Previous Value',
        size: 180,
        Cell: ({ row, cell }) => renderHistoryValue(row.original.field, cell.getValue()),
      },
      {
        accessorKey: 'newValue',
        header: 'New Value',
        size: 180,
        Cell: ({ row, cell }) => renderHistoryValue(row.original.field, cell.getValue()),
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data,
    localization: MRT_Localization_EN,
    getRowId: (row) => String(row?.id ?? buildHistoryRowId(row)),
    enableRowSelection: true,
    enableStickyHeader: true,
    enableColumnResizing: true,
    enableColumnOrdering: true,
    enableColumnPinning: true,
    enableColumnFilters: true,
    enableGlobalFilter: false,
    columnFilterDisplayMode: 'popover',
    initialState: {
      density: 'compact',
      pagination: { pageIndex: 0, pageSize: 25 },
    },
    state: {
      columnFilters,
      sorting,
      columnVisibility,
      columnOrder,
      columnPinning,
      isLoading,
    },
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnPinningChange: setColumnPinning,
    renderColumnActionsMenuItems: ({ column, closeMenu, internalColumnMenuItems, table: mrtTable }) => {
      const canFilter = column.getCanFilter?.();
      if (!canFilter) return internalColumnMenuItems;

      const addFilterItem = (
        <MenuItem
          key="add-filter"
          onClick={() => {
            setPendingFilterColumnId(column.id);
            closeMenu?.();
          }}
        >
          <FilterAltIcon fontSize="small" sx={{ mr: 1 }} />
          Add Filter
        </MenuItem>
      );

      const internalItems = Array.isArray(internalColumnMenuItems)
        ? [...internalColumnMenuItems]
        : internalColumnMenuItems
          ? [internalColumnMenuItems]
          : [];

      const clearFilterLabel = mrtTable?.options?.localization?.clearFilter || 'Clear Filter';
      const getMenuItemLabel = (node) => {
        if (!node) return '';
        if (typeof node === 'string') return node;
        if (Array.isArray(node)) return node.map(getMenuItemLabel).join('');
        if (typeof node === 'object') {
          if (node.props?.label) return node.props.label;
          if (node.props?.children) return getMenuItemLabel(node.props.children);
        }
        return '';
      };
      const normalizedClearFilterLabel = clearFilterLabel
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
      const clearFilterIndex = internalItems.findIndex((item) => {
        const label = getMenuItemLabel(item);
        return label.replace(/\s+/g, ' ').trim().toLowerCase() === normalizedClearFilterLabel;
      });

      if (clearFilterIndex === -1) return [addFilterItem, ...internalItems];
      internalItems.splice(clearFilterIndex, 0, addFilterItem);
      return internalItems;
    },
    muiTableHeadCellProps: ({ column }) => {
      const isSorted = Boolean(column.getIsSorted?.());
      return {
        sx: {
          '& .Mui-TableHeadCell-Content button[aria-label*="Filter by"]': {
            opacity: 0,
            pointerEvents: 'none',
          },
          '& .Mui-TableHeadCell-Content-Actions .MuiIconButton-root': {
            display: 'inline-flex',
          },
          '& .MuiTableSortLabel-root': {
            display: isSorted ? 'inline-flex' : 'none',
          },
        },
      };
    },
    muiTableContainerProps: {
      sx: { maxHeight: { xs: 460, md: 620 } },
    },
    renderTopToolbarCustomActions: () => null,
    renderToolbarInternalActions: () => null,
  });

  useEffect(() => {
    if (!pendingFilterColumnId || !table) return undefined;
    return scheduleMrtFilterPopoverOpen({
      table,
      columnId: pendingFilterColumnId,
      buttonSelector: 'button[aria-label*="Filter by"]',
      onComplete: () => setPendingFilterColumnId(null),
    });
  }, [pendingFilterColumnId, table]);

  return (
    <Box sx={{ width: '100%' }}>
      {!data.length && !isLoading ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No history rows match the current Dealer360 filters.
        </Alert>
      ) : null}
      {data.length ? (
        <Box sx={{ mb: 1 }}>
          <Chip
            size="small"
            variant="outlined"
            label={`${counts?.total ?? data.length} change${(counts?.total ?? data.length) === 1 ? '' : 's'} • ${
              counts?.selectionStatus ?? 0
            } selection status`}
          />
        </Box>
      ) : null}
      <Paper sx={{ width: '100%' }}>
        <MaterialReactTable table={table} />
      </Paper>
    </Box>
  );
};

export default DealerHistoryMrtTable;
